/**
 * Scheme rules for the planner's order screen.
 *
 * `POST portfolio_planner/generate/` picks funds without ever reading the order
 * gateway's rules for them, so every ISIN it returns has to be looked up before
 * anything can be placed. One call per DISTINCT isin — a plan that buys the
 * same fund for two objectives is one scheme, not two.
 *
 * A failed lookup is NOT treated as "no rules" — it is reported, because
 * placing an order whose limits could not be read is exactly the case that
 * fails at the gateway after the money has moved.
 *
 * The user platform does this with React Query's `useQueries`; this project has
 * no React Query, so the same contract is met with a small in-module cache and
 * a single effect. `fetchMFSchemeDetails` is the admin panel's own authoritative
 * per-ISIN wrapper, which already rejects a response whose ISIN does not match.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  fetchMFSchemeDetails,
  getMFErrorMessage,
} from "../../Api/mfAdminInvestments";
import type { MFSchemeDetails } from "../../Api/mfAdminInvestments";

export interface SchemeRules {
  byIsin: Record<string, MFSchemeDetails | undefined>;
  /** ISINs whose lookup failed. Those orders cannot be validated, so cannot go. */
  failed: string[];
  failureReasons: Record<string, string>;
  isLoading: boolean;
  loaded: number;
  total: number;
  /** Re-runs only the lookups that failed. */
  retryFailed: () => void;
}

/** Shared across mounts so re-entering the orders screen is free. */
const cache = new Map<string, MFSchemeDetails>();

export const usePlannerSchemes = (isins: string[]): SchemeRules => {
  const distinct = useMemo(() => {
    const seen: string[] = [];
    isins.forEach((isin) => {
      const value = String(isin || "").trim();
      if (value && seen.indexOf(value) < 0) seen.push(value);
    });
    return seen;
  }, [isins]);

  const key = distinct.join("|");
  const [byIsin, setByIsin] = useState<Record<string, MFSchemeDetails | undefined>>({});
  const [failureReasons, setFailureReasons] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<string[]>([]);
  const [retrySignal, setRetrySignal] = useState(0);
  const activeRequest = useRef(0);

  useEffect(() => {
    const requestId = activeRequest.current + 1;
    activeRequest.current = requestId;

    const seeded: Record<string, MFSchemeDetails | undefined> = {};
    const outstanding: string[] = [];
    distinct.forEach((isin) => {
      const cached = cache.get(isin);
      if (cached) seeded[isin] = cached;
      else outstanding.push(isin);
    });
    setByIsin(seeded);
    setFailureReasons({});
    setPending(outstanding);
    if (!outstanding.length) return;

    outstanding.forEach((isin) => {
      fetchMFSchemeDetails(isin)
        .then((details) => {
          if (activeRequest.current !== requestId) return;
          cache.set(isin, details);
          setByIsin((current) => ({ ...current, [isin]: details }));
        })
        .catch((error) => {
          if (activeRequest.current !== requestId) return;
          setFailureReasons((current) => ({
            ...current,
            [isin]: getMFErrorMessage(error, "The scheme rules could not be loaded."),
          }));
        })
        .then(() => {
          if (activeRequest.current !== requestId) return;
          setPending((current) => current.filter((value) => value !== isin));
        });
    });
    // `key` is the stable identity of `distinct`; retrySignal re-runs failures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, retrySignal]);

  const failed = distinct.filter(
    (isin) => !byIsin[isin] && Boolean(failureReasons[isin])
  );

  return {
    byIsin,
    failed,
    failureReasons,
    isLoading: pending.length > 0,
    loaded: distinct.filter((isin) => Boolean(byIsin[isin])).length,
    total: distinct.length,
    retryFailed: () => {
      failed.forEach((isin) => cache.delete(isin));
      setRetrySignal((value) => value + 1);
    },
  };
};
